/**
 * Action Center — type-specific detail tray (DEV-1201,
 * POLICY_ACTION_CENTER.md). Opened on Inbox row click; the row carries no
 * action buttons, so all affordances live here, customized per ActionKind.
 *
 * Data-entry trays reuse the existing MetricEntryGrid section components so
 * there is one entry surface, not a parallel implementation.
 */

import React, { useState } from "react";
import { uploadFile } from "../../api/fileStorage";
import { mediaSrc } from "../../api/mediaUrl";
// Note: addMiniProjectTaskEvidence / removeMiniProjectTaskEvidence are
// invoked by the PARENT (ActionCenterInbox) via handlers, so they don't
// need to be imported here. Kept as a doc-comment to make the seam visible.
import { excludeHse } from "../../Data/missionSubkpiFilter";
import ScheduleDataEntry from "../../Components/MetricEntryGrid/ScheduleDataEntry";
import BudgetDataEntry from "../../Components/MetricEntryGrid/BudgetDataEntry";
import MissionDataEntry from "../../Components/MetricEntryGrid/MissionDataEntry";
import RhythmStepList from "../../Components/RhythmStepList/RhythmStepList";
import { cadenceAnchorSummary } from "../Engagements/cadenceFormatter";
import RoleSummary, {
  useInvitationRoleSummary,
  type RoleSummaryResponsibility,
} from "../../Components/RoleSummary/RoleSummary";
import { getCurrentUserGuid } from "../../api/client";
import { getHydratedSecurityUser } from "../../security/securityAdapter";
import { statusKey, type ActionItem } from "./actionItems";
import styles from "./ActionCenterInbox.module.css";

// DEV-1257-followup. Evidence upload policy — mirror of the backend
// FileStorageController.CategoryPolicies["mp-task-evidence"] so the user
// gets immediate feedback before bytes leave the browser. Backend is the
// security gate; this is UX (and a fast-fail when someone picks a movie).
const EVIDENCE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const EVIDENCE_ALLOWED_EXTS = new Set([
  // Images
  ".jpg", ".jpeg", ".png", ".gif", ".heic", ".webp",
  // Documents
  ".pdf", ".doc", ".docx", ".txt", ".rtf", ".odt",
  // Spreadsheets
  ".xls", ".xlsx", ".csv", ".ods",
  // Presentations
  ".ppt", ".pptx", ".odp",
]);
// `accept` attribute on <input type="file"> — same list, comma-joined.
// The OS picker uses this to filter the file list (best-effort; not a
// security gate — users can still pick anything via drag-drop on platforms
// that ignore it).
const EVIDENCE_ACCEPT_ATTR = Array.from(EVIDENCE_ALLOWED_EXTS).join(",");

/** Returns null if the file passes the evidence policy, otherwise an error key. */
function validateEvidenceFile(
  file: File,
): { error: "too_large" | "type_not_allowed"; detail: string } | null {
  if (file.size > EVIDENCE_MAX_BYTES) {
    return {
      error: "too_large",
      detail: `${file.name} (${Math.round(file.size / 1024 / 1024)} MB)`,
    };
  }
  const lastDot = file.name.lastIndexOf(".");
  const ext = lastDot >= 0 ? file.name.slice(lastDot).toLowerCase() : "";
  if (!EVIDENCE_ALLOWED_EXTS.has(ext)) {
    return { error: "type_not_allowed", detail: file.name };
  }
  return null;
}

/**
 * DEV-1256. Open the ChatTray (bottom-right floating panel) with a DM
 * to the given user. App-level listener in App.tsx catches the event,
 * sets chatTrayOpen=true, and forwards the user GUID to ChatTray which
 * dedupes or creates the conversation. Keeps the user in the Action
 * Center instead of routing them to /messages.
 */
function openChatTrayWith(userGuid: string) {
  window.dispatchEvent(
    new CustomEvent("l4e:open-chat-with", { detail: { userGuid } }),
  );
}

type T = (key: string, vars?: Record<string, any>) => string;

export interface ActionItemTrayHandlers {
  acceptInvitation: (item: ActionItem, note: string) => void;
  declineInvitation: (item: ActionItem, note: string) => void;
  approveEvent: (item: ActionItem) => void;
  rejectEvent: (item: ActionItem) => void;
  acknowledge: (item: ActionItem) => void;
  completeRhythm: (item: ActionItem) => void;
  /** DEV-1368. Mark a Responsible Gantt task done from the do-list tray. */
  completeRaci: (item: ActionItem) => void;
  /** DEV-1257-followup. Days parameter lets the tray expose 1d/3d/7d chips
   *  for cadences ranging from daily through monthly+. Default callers can
   *  still pass 7 to preserve the prior single-button behavior. */
  snoozeRhythm: (item: ActionItem, days: number) => void;
  /** DEV-1257. Mini-project task: one-click Done with optional completion
   *  note. Evidence attachments are persisted SEPARATELY via
   *  addMpTaskEvidence (upload-on-pick), so by the time this fires the
   *  task already has the keys it needs. The newEvidenceKeys param is
   *  retained for backward compat but the tray always passes []. */
  completeMpTask: (item: ActionItem, note: string, newEvidenceKeys: string[]) => void;
  /** DEV-1257-followup. Add newly-uploaded evidence keys to a mini-project
   *  task. Fires immediately on file pick (upload-on-pick design) so
   *  attachments survive across reloads / tab switches. Refetches the
   *  task so the existing-evidence chips render the new keys. */
  addMpTaskEvidence: (item: ActionItem, keys: string[]) => Promise<void>;
  /** DEV-1257-followup. Remove an already-attached evidence key from a
   *  mini-project task. Server uses CanUpdateOwnTaskStatus so assignees
   *  can fix their own uploads. Tray confirms before calling. */
  removeMpTaskEvidence: (item: ActionItem, key: string) => void;
  /** Onboarding "Setup" tasks: navigate/open the surface to complete it. */
  openSystemCta: (item: ActionItem) => void;
  /** Contextual navigation from the tray. */
  openCommunity: (guid: string) => void;
  openProject: (guid: string) => void;
  /** DEV-1414. Open a user's profile — identity verification for
   *  membership join requests (approver confirms the requester). */
  openProfile: (guid: string) => void;
  /** DEV-1257. Mini-Projects have their own route namespace (`/mini-projects/{guid}`)
   *  and no parent project FK, so they need a separate navigation handler from
   *  full projects. */
  openMiniProject: (guid: string) => void;
  openEngagement: () => void;
  /** Receives save coverage from the entry grids so the host can tell a FULL
   *  save from a PARTIAL one — only full saves resolve the item (DEV-1256). */
  onDataSaved: (info?: { savedCount: number; totalCount: number }) => void;
}

interface Props {
  item: ActionItem | null;
  open: boolean;
  busy: boolean;
  t: T;
  handlers: ActionItemTrayHandlers;
  onClose: () => void;
}

const ActionItemTray: React.FC<Props> = ({ item, open, busy, t, handlers, onClose }) => {
  const [note, setNote] = useState("");
  // DEV-1257-followup. Upload-on-pick (no more lazy upload). When the user
  // selects files we upload immediately and persist the storage keys via
  // handlers.addMpTaskEvidence. `files` here is just the in-flight name
  // list shown as transient "Uploading…" rows — it empties when uploads
  // complete and the new keys appear in the task's evidenceAttachmentKeys
  // (which renders as proper deletable chips above).
  const [files, setFiles] = useState<File[]>([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Reset per-item state whenever a different item opens.
  React.useEffect(() => {
    setNote("");
    setFiles([]);
    setUploadBusy(false);
    setUploadError(null);
  }, [item?.id]);

  // Invitation tray: the role's description + responsibilities resolve via the
  // SHARED RoleSummary resolution (DEV-1424 → extracted for DEV-1425: slot →
  // definition → rhythm details, best-effort) so the pre-accept preview and
  // the post-accept reference view can never drift.
  const invitePayloadForPreview = (item?.workflow?.payload as any) || {};
  const inviteCommunityGuid =
    item?.kind === "invitation"
      ? invitePayloadForPreview.community_entity_guid ||
        (item?.workflow?.target_type === "community" ? item?.workflow?.target_guid : null)
      : null;
  const inviteSlotGuid =
    item?.kind === "invitation" ? invitePayloadForPreview.role_slot_guid || null : null;
  const {
    description: inviteRoleDescription,
    responsibilities: inviteResponsibilities,
  } = useInvitationRoleSummary(
    item?.kind === "invitation",
    inviteCommunityGuid ? String(inviteCommunityGuid) : null,
    inviteSlotGuid ? String(inviteSlotGuid) : null,
    item?.id,
  );

  const wide = item?.kind === "data";

  // DEV-1257-followup. Status-colored left accent on the tray mirrors the
  // inbox row's bar so the visual cue persists through the click. Same
  // mapping the row uses (see ActionCenterInbox visibleItems render).
  const statusAccentClass = (() => {
    if (!item) return "";
    if (item.status === "overdue") return styles.traySCritical;
    if (item.status === "due" || item.status === "needs_response") return styles.traySWarning;
    if (item.status === "ongoing") return styles.traySOnTrack;
    return styles.traySNeutral;
  })();
  // Matching pill class for the status chip in the meta prose line.
  const statusPillClass = (() => {
    if (!item) return "";
    if (item.status === "overdue") return styles.trayStatusPillCritical;
    if (item.status === "due" || item.status === "needs_response") return styles.trayStatusPillWarning;
    if (item.status === "ongoing") return styles.trayStatusPillOnTrack;
    return styles.trayStatusPillNeutral;
  })();

  /**
   * DEV-1257-followup. Upload-on-pick. Fires immediately when the user
   * selects files: each file uploads to MinIO via FileStorageController,
   * then the resulting keys are persisted via handlers.addMpTaskEvidence.
   * That triggers a refetch and the new keys appear in the existing-
   * evidence chip list above with the delete × already wired up.
   *
   * Earlier "wait until Done" design caused user confusion — uploads
   * silently dropped if the user didn't transition the task. (Confirmed
   * via SQL: cardinality = 0 on tasks that the user thought they had
   * attached to.) Upload-on-pick costs us occasional orphan MinIO objects
   * if a user picks then bails; that's fine — far smaller cost than the
   * "where did my attachment go?" failure mode.
   */
  const handleFilesPicked = React.useCallback(
    async (picked: File[]) => {
      if (!item || item.kind !== "mp_task" || !item.mpTask || picked.length === 0) return;
      setUploadError(null);

      // DEV-1257-followup. Pre-validate locally so we can reject without
      // burning bandwidth. If ANY file fails, reject the whole batch and
      // surface a specific error — easier mental model than partial uploads
      // (user has to fix some, retry whole picker, etc.). Backend will
      // reject too if anything slips through (different browsers handle
      // the accept attribute inconsistently).
      const rejects = picked
        .map((f) => ({ file: f, reason: validateEvidenceFile(f) }))
        .filter((r) => r.reason !== null);
      if (rejects.length > 0) {
        const tooLarge = rejects.filter((r) => r.reason!.error === "too_large");
        const wrongType = rejects.filter((r) => r.reason!.error === "type_not_allowed");
        const messages: string[] = [];
        if (tooLarge.length > 0) {
          messages.push(
            t("actionCenter.inbox.tray.taskFileTooLarge", {
              names: tooLarge.map((r) => r.reason!.detail).join(", "),
              maxMb: Math.round(EVIDENCE_MAX_BYTES / 1024 / 1024),
              defaultValue: `Too large (>${Math.round(EVIDENCE_MAX_BYTES / 1024 / 1024)} MB): ${tooLarge.map((r) => r.reason!.detail).join(", ")}`,
            } as never),
          );
        }
        if (wrongType.length > 0) {
          messages.push(
            t("actionCenter.inbox.tray.taskFileTypeBlocked", {
              names: wrongType.map((r) => r.reason!.detail).join(", "),
              defaultValue: `File type not allowed: ${wrongType.map((r) => r.reason!.detail).join(", ")}. Allowed: photos, PDFs, documents.`,
            } as never),
          );
        }
        setUploadError(messages.join("  "));
        return;
      }

      setFiles(picked); // surface the in-flight list while uploading
      setUploadBusy(true);
      try {
        const taskGuid = item.mpTask.task.taskGuid;
        const results = await Promise.all(
          picked.map((f) => uploadFile(f, "mp-task-evidence", taskGuid)),
        );
        const newKeys = results.map((r) => r.key);
        await handlers.addMpTaskEvidence(item, newKeys);
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploadBusy(false);
        setFiles([]); // clear in-flight list; new chips render from the refetched task
      }
    },
    [item, handlers, t],
  );

  /**
   * DEV-1257-followup. Done now just transitions + writes the completion
   * note. Evidence is already on the task by this point thanks to
   * upload-on-pick. The newEvidenceKeys param is retained on the handler
   * for backward compat but we always pass [].
   */
  const handleMpTaskDone = React.useCallback(() => {
    if (!item || item.kind !== "mp_task" || !item.mpTask) return;
    handlers.completeMpTask(item, note.trim(), []);
  }, [item, note, handlers]);

  // DEV-1257-followup. Inline-link version of the source field for the prose
  // meta line. Routes by kind: rhythm/standing → community, mp_task → MP, else
  // plain text. Keeps Q3=(b) "navigation lives inline in the body" intact.
  const renderSourceInline = () => {
    if (!item) return null;
    if ((item.kind === "rhythm" || item.kind === "standing") && item.communityGuid) {
      return (
        <button
          type="button"
          className={styles.inlineLink}
          onClick={() => handlers.openCommunity(item.communityGuid!)}
        >
          {item.source}
        </button>
      );
    }
    if (item.kind === "mp_task" && item.mpTask) {
      return (
        <button
          type="button"
          className={styles.inlineLink}
          onClick={() => handlers.openMiniProject(item.mpTask!.task.miniProjectGuid)}
        >
          {item.source}
        </button>
      );
    }
    return <span>{item.source}</span>;
  };

  return (
    <aside
      className={`${styles.tray} ${statusAccentClass} ${open ? styles.trayOpen : ""} ${wide ? styles.trayWide : ""}`}
      aria-hidden={!open}
    >
      <button
        type="button"
        className={styles.trayClose}
        aria-label={t("actionCenter.inbox.close")}
        onClick={onClose}
      >
        {/* DEV-1257-followup. Unicode × instead of <i className="ti ti-x">
            because the Tabler webfont is not actually loaded in the app
            (13 ti-* refs codebase-wide all render blank). RhythmTaskTray
            uses the same Unicode × pattern. Filed as a follow-up to either
            load Tabler properly or replace the remaining ti-* refs. */}
        ×
      </button>

      {item && (
        <>
          <div className={styles.typeChip} style={{ display: "inline-block" }}>
            {t(item.typeKey)}
          </div>
          <div className={styles.trayTitle}>{item.subject}</div>
          {/* DEV-1257-followup. Prose meta line replaces the dl. Source
              becomes an inline link to the underlying community/MP. */}
          <p className={styles.trayMetaProse}>
            {renderSourceInline()}
            {item.role && (
              <>
                <span className={styles.trayMetaSep}>·</span>
                <span className={styles.trayMetaStrong}>{item.role}</span>
              </>
            )}
            {item.dateLabel && item.dateLabel !== "—" && (
              <>
                <span className={styles.trayMetaSep}>·</span>
                <span>{item.dateLabel}</span>
              </>
            )}
            <span className={styles.trayMetaSep}>·</span>
            <span className={`${styles.trayStatusPill} ${statusPillClass}`}>
              {t(statusKey(item.status))}
            </span>
          </p>
          <div className={styles.trayBody}>
            {renderBody(item, {
              note,
              setNote,
              files,
              uploadBusy,
              uploadError,
              onMpTaskDone: handleMpTaskDone,
              onFilesPicked: handleFilesPicked,
              busy: busy || uploadBusy,
              t,
              handlers,
              inviteRoleDescription,
              inviteResponsibilities,
            })}
          </div>
        </>
      )}
    </aside>
  );
};

function renderBody(
  item: ActionItem,
  ctx: {
    note: string;
    setNote: (v: string) => void;
    /** Files currently uploading (set during handleFilesPicked, cleared
     *  once uploads resolve). Rendered as "Uploading: name.pdf (12 KB)"
     *  rows. After upload the keys land in task.evidenceAttachmentKeys
     *  and render as proper deletable chips above. */
    files: File[];
    uploadBusy: boolean;
    uploadError: string | null;
    onMpTaskDone: () => void;
    onFilesPicked: (picked: File[]) => Promise<void>;
    busy: boolean;
    t: T;
    handlers: ActionItemTrayHandlers;
    /** DEV-1424 — the role's own description for the "Review before you
     *  accept" preview (empty for non-invitation items). */
    inviteRoleDescription: string;
    /** DEV-1424 — the role's responsibilities (standing + recurring duty
     *  names, each tagged) for the invitation preview. */
    inviteResponsibilities: RoleSummaryResponsibility[];
  },
): React.ReactNode {
  const { t, handlers, busy, inviteRoleDescription, inviteResponsibilities } = ctx;

  // Resolved items are read-only for CONSUMABLE actions (approve/reject/
  // acknowledge — re-firing is meaningless or harmful), but the description
  // keeps its reference value and NAVIGATION actions stay useful after
  // completion (Skip 2026-07-08): a completed "Complete your profile" still
  // wants its Edit-profile button; a read acknowledgment can be re-read.
  if (item.status === "resolved") {
    if (item.kind === "system") {
      // Getting-started / onboarding milestone rows: keep body + the nav
      // CTA (Edit profile, Explore, Visit the feed) — completion is
      // system-detected, so the CTA was never a consumable act.
      const rms = (item.milestone || {}) as {
        nudgeKey?: string;
        nudgeText?: string;
        ctaLabelKey?: string;
        ctaRoute?: string;
      };
      const rnudge =
        rms.nudgeText || (rms.nudgeKey ? t(rms.nudgeKey) : t("actionCenter.inbox.tray.systemBody"));
      // DEV-1450 security binding: a COMPLETED 2FA task never re-offers
      // enrollment (no fresh QR for an enrolled user) — it reads as the
      // outcome. Re-enrollment/rotation lives in Account Settings behind
      // re-auth, never as an inbox affordance (POLICY_ACCOUNT_SECURITY_
      // LADDER; architect 2026-07-10).
      const is2fa = (rms as any).milestone === "enable_2fa";
      return (
        <>
          <p>{rnudge}</p>
          {(is2fa || rms.ctaRoute || rms.ctaLabelKey) && (
            <div className={styles.trayFooter}>
              <button
                type="button"
                className={styles.btn}
                disabled={is2fa || busy}
                onClick={() => handlers.openSystemCta(item)}
                style={{ marginInlineStart: "auto" }}
              >
                {is2fa
                  ? t("actionCenter.inbox.doneOutcome.twoFaEnabled")
                  : rms.ctaLabelKey
                    ? t(rms.ctaLabelKey)
                    : t("actionCenter.inbox.action.go")}
              </button>
            </div>
          )}
        </>
      );
    }
    if (item.kind === "notice") {
      // Keep body + pointer link. The read-me-first content is durable and
      // re-openable; acknowledge-style acts render as a disabled
      // "Acknowledged" so the record reads clearly.
      const rms = (item.milestone || {}) as {
        nudgeKey?: string;
        nudgeText?: string;
        communityGuid?: string;
      };
      // Durable content stays re-openable after resolution: the welcome
      // screen (read_me_first) and the Contributor celebration popup.
      const reopenable =
        item.checklistKey === "read_me_first" ||
        item.checklistKey === "promotion_notice";
      return (
        <>
          <p>{rms.nudgeText || (rms.nudgeKey ? t(rms.nudgeKey) : t("actionCenter.inbox.tray.noticeBody"))}</p>
          {rms.communityGuid && (
            <div className={styles.trayLinks}>
              <button
                type="button"
                className={styles.trayInlineLink}
                onClick={() => handlers.openCommunity(String(rms.communityGuid))}
              >
                ↗ {t("actionCenter.inbox.action.openCommunity")}
              </button>
            </div>
          )}
          <div className={styles.trayFooter}>
            <button
              type="button"
              className={styles.btn}
              disabled={!reopenable || busy}
              onClick={() => handlers.acknowledge(item)}
              style={{ marginInlineStart: "auto" }}
            >
              {item.checklistKey === "promotion_notice"
                ? t("actionCenter.inbox.gettingStarted.promotion.viewWelcome")
                : reopenable
                  ? t("actionCenter.inbox.gettingStarted.readMeFirst.cta")
                  : t("actionCenter.inbox.action.acknowledged")}
            </button>
          </div>
        </>
      );
    }
    // Consequence-bearing kinds (approvals, invitations, data, rhythms):
    // unchanged — no re-action affordances.
    return <p>{t("actionCenter.inbox.tray.resolvedBody")}</p>;
  }

  if (item.kind === "invitation") {
    // The producer writes a full sentence ("X invited you to the role Y on
    // community Z") into the workflow description; surface it so the invitee
    // knows the role + community. Fall back to role_label, then the generic
    // blurb. (DEV-1368 — invitations were showing no role/community.)
    const inviteDesc =
      (item.workflow?.description || "").trim() ||
      ((item.workflow?.payload as any)?.role_label
        ? `${t("actionCenter.inbox.tray.invitationRoleLead")} ${(item.workflow?.payload as any).role_label}`
        : "");
    const payload = (item.workflow?.payload as any) || {};
    const w = item.workflow;
    const communityGuid =
      payload.community_entity_guid || (w?.target_type === "community" ? w?.target_guid : null);
    const miniGuid = payload.mini_project_guid || null;
    const projGuid = miniGuid || (w?.target_type === "project" ? w?.target_guid : null);
    return (
      <>
        {inviteDesc ? <p>{inviteDesc}</p> : null}
        {(communityGuid || projGuid) && (
          <div className={styles.trayLinks}>
            {communityGuid && (
              <button
                type="button"
                className={styles.trayInlineLink}
                onClick={() => handlers.openCommunity(String(communityGuid))}
              >
                ↗ {t("actionCenter.inbox.action.openCommunity")}
              </button>
            )}
            {projGuid && (
              <button
                type="button"
                className={styles.trayInlineLink}
                onClick={() =>
                  miniGuid
                    ? handlers.openMiniProject(String(projGuid))
                    : handlers.openProject(String(projGuid))
                }
              >
                ↗ {t("actionCenter.inbox.action.openProject")}
              </button>
            )}
          </div>
        )}
        {/* DEV-1424 — "Review this role before you accept": rendered by the
            SHARED RoleSummary component (DEV-1425 extraction) so the preview
            and the post-accept reference view render one canonical record. */}
        <RoleSummary
          variant="preview"
          heading={t("actionCenter.inbox.tray.reviewRoleHeading")}
          description={inviteRoleDescription}
          responsibilities={inviteResponsibilities}
        />
        <p className={styles.trayHint}>{t("actionCenter.inbox.tray.invitationBody")}</p>
        <textarea
          className={styles.replyNote}
          rows={3}
          maxLength={2000}
          placeholder={t("actionCenter.inbox.tray.replyNotePlaceholder")}
          value={ctx.note}
          onChange={(e) => ctx.setNote(e.target.value)}
        />
        {/* DEV-1257-followup. Decline (secondary, muted) left + Accept
            (primary) right. Declining an invitation is benign — muted
            grey, not danger-red (that's reserved for Reject on approvals). */}
        <div className={styles.trayFooter}>
          <div className={styles.trayFooterSecondary}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnMuted}`}
              disabled={busy}
              onClick={() => handlers.declineInvitation(item, ctx.note.trim())}
            >
              {t("actionCenter.inbox.action.decline")}
            </button>
          </div>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy}
            onClick={() => handlers.acceptInvitation(item, ctx.note.trim())}
          >
            {t("actionCenter.inbox.action.accept")}
          </button>
        </div>
      </>
    );
  }

  if (item.kind === "approval" || item.kind === "governance") {
    // DEV-1414 — membership join requests carry an identity-verification
    // block: the approver confirms the requester is who they say they are.
    // Email + avatar resolve LIVE from the hydrated security-users list
    // (never denormalized into the immutable workflow payload); the profile
    // link routes to the requester's page for the full picture (home
    // community, engagements, who invited them).
    const isMembershipRequest =
      item.workflow?.action_type === "community.membership_request";
    const requesterGuid = isMembershipRequest
      ? String((item.workflow?.payload as any)?.user_guid || "")
      : "";
    const requester = requesterGuid ? getHydratedSecurityUser(requesterGuid) : null;
    const requesterName =
      String(
        requester?.display_name ||
          requester?.name ||
          (item.workflow?.payload as any)?.user_display_name ||
          "",
      ).trim();
    const requesterEmail = String(requester?.email || "").trim();
    const requestMessage = isMembershipRequest
      ? String((item.workflow?.payload as any)?.custom_message || "").trim()
      : "";
    return (
      <>
        {isMembershipRequest && requesterGuid ? (
          <div className={styles.trayLinks}>
            <p>
              <b>{requesterName || requesterGuid.slice(0, 8)}</b>
              {requesterEmail ? ` · ${requesterEmail}` : ""}
            </p>
            {requestMessage ? <p>“{requestMessage}”</p> : null}
            <button
              type="button"
              className={styles.trayInlineLink}
              onClick={() => handlers.openProfile(requesterGuid)}
            >
              ↗ {t("actionCenter.inbox.action.viewProfile")}
            </button>
          </div>
        ) : null}
        <p>{t("actionCenter.inbox.tray.reviewBody")}</p>
        {/* Reject is danger-red — rejecting a budget / governance proposal
            has real consequences (unlike declining an invitation). */}
        <div className={styles.trayFooter}>
          <div className={styles.trayFooterSecondary}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnDanger}`}
              disabled={busy}
              onClick={() => handlers.rejectEvent(item)}
            >
              {t("actionCenter.inbox.action.reject")}
            </button>
          </div>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy}
            onClick={() => handlers.approveEvent(item)}
          >
            {t("actionCenter.inbox.action.approve")}
          </button>
        </div>
      </>
    );
  }

  if (item.kind === "notice") {
    // Chunk A (§7.7 r2 / §7.9) — synthetic onboarding notices carry their body
    // + pointer in item.milestone: the guidelines row links to the org page
    // (pointer, never the content) and the promotion Notice carries the
    // celebration copy. Workflow-backed notices keep the generic body.
    const ms = (item.milestone || {}) as {
      nudgeKey?: string;
      nudgeText?: string;
      communityGuid?: string;
    };
    return (
      <>
        <p>{ms.nudgeText || (ms.nudgeKey ? t(ms.nudgeKey) : t("actionCenter.inbox.tray.noticeBody"))}</p>
        {ms.communityGuid && (
          <div className={styles.trayLinks}>
            <button
              type="button"
              className={styles.trayInlineLink}
              onClick={() => handlers.openCommunity(String(ms.communityGuid))}
            >
              ↗ {t("actionCenter.inbox.action.openCommunity")}
            </button>
          </div>
        )}
        <div className={styles.trayFooter}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy}
            onClick={() => handlers.acknowledge(item)}
            style={{ marginInlineStart: "auto" }}
          >
            {/* Read-this rows open their CONTENT (modal), not a bare
                acknowledge — label the button honestly. */}
            {item.checklistKey === "platform_standards"
              ? t("actionCenter.inbox.gettingStarted.standards.cta")
              : item.checklistKey === "read_me_first"
                ? t("actionCenter.inbox.gettingStarted.readMeFirst.cta")
                : item.checklistKey === "org_guidelines" ||
                    item.checklistKey === "org_guidelines_member"
                  ? t("actionCenter.inbox.gettingStarted.readGuidelines.cta")
                  : item.checklistKey === "promotion_notice"
                    ? t("actionCenter.inbox.gettingStarted.promotion.viewWelcome")
                    : t("actionCenter.inbox.action.acknowledge")}
          </button>
        </div>
      </>
    );
  }

  if (item.kind === "rhythm" || item.kind === "standing") {
    const standing = item.kind === "standing";
    const canSnooze =
      !standing &&
      item.instance != null &&
      item.status !== "snoozed" &&
      (item.instance.snoozeCount ?? 0) < 2;
    // DEV-1251 P3. Steps + contact come from RoleRhythmInstanceResponse
    // (extended in P1 / DEV-1252). Both are optional on the instance.
    const steps = item.instance?.steps ?? [];
    const contact = item.instance?.contactUser ?? null;
    // DEV-1251 P3. Cadence anchor — borrow the shared formatter so the
    // Action Center matches the Engagements summary exactly.
    const anchorText = cadenceAnchorSummary(
      item.cadenceType ?? null,
      item.instance?.cadenceAnchor ?? null,
    );
    // Self-contact suppresses the Message button (per DEV-1240 P3).
    const isSelfContact =
      contact != null &&
      contact.userGuid.toUpperCase() === getCurrentUserGuid().toUpperCase();
    // DEV-1257-followup. Cadence + lead-time collapse into one tertiary
    // sub-line under the description, matching the locked redesign.
    const cadenceLine = (() => {
      if (standing) return null;
      const parts: string[] = [];
      if (item.cadenceType) {
        const key = `engagements.rhythmCadence.${item.cadenceType}`;
        const loc = t(key);
        parts.push(loc === key ? item.cadenceType : loc);
      }
      if (anchorText) parts.push(anchorText);
      if (item.leadTimeDays != null) {
        parts.push(t("actionCenter.inbox.tray.leadTime", { days: item.leadTimeDays }));
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    })();
    return (
      <>
        <p style={{ margin: 0 }}>
          {item.description
            ? item.description
            : standing
              ? t("actionCenter.inbox.tray.standingBody")
              : t("actionCenter.inbox.tray.rhythmBody")}
        </p>
        {cadenceLine && (
          <p style={{ fontSize: 11, color: "var(--text-tertiary)", marginBlock: "4px 0" }}>
            {cadenceLine}
          </p>
        )}

        {/* DEV-1257-followup. Contact restored to a lifted card per Skip's
            feedback — avatar + name + sub-label + Message button. Same
            messaging affordance as before, cleaner styling. */}
        {contact && (
          <div className={styles.trayContactCard}>
            {contact.avatarUrl ? (
              <img
                src={mediaSrc(contact.avatarUrl) ?? undefined}
                alt=""
                className={styles.trayContactCardAvatar}
              />
            ) : (
              <span className={styles.trayContactCardAvatar} aria-hidden="true">
                {contact.name.trim().charAt(0).toUpperCase() || "?"}
              </span>
            )}
            <span className={styles.trayContactCardName}>
              {contact.name}
              <span className={styles.trayContactCardSub}>
                {t("actionCenter.inbox.tray.contactSub", { defaultValue: "Contact for this task" } as never)}
              </span>
            </span>
            {!isSelfContact && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnMuted}`}
                onClick={() => openChatTrayWith(contact.userGuid)}
              >
                <i className="ti ti-message" aria-hidden="true" />{" "}
                {t("actionCenter.inbox.tray.message", { defaultValue: "Message" } as never)}
              </button>
            )}
          </div>
        )}

        {steps.length > 0 && (
          <div className={styles.trayBodySection}>
            <div className={styles.trayBodySectionLabel}>
              {t("actionCenter.inbox.tray.steps", { defaultValue: "Steps" } as never)}
            </div>
            <RhythmStepList steps={steps} />
          </div>
        )}

        {/* DEV-1257-followup. Engagements nav inlined as a small link near
            the bottom of the body (Q3=(b) lock). The community link is now
            in the meta prose line at the top of the tray, so the standalone
            Open Community button drops. */}
        <button
          type="button"
          className={styles.trayInlineLink}
          onClick={() => handlers.openEngagement()}
        >
          ↗ {t("actionCenter.inbox.action.openEngagement")}
        </button>

        {/* DEV-1257-followup. Structured footer — SNOOZE 1d/3d/7d chips
            on the left (rhythm only), primary Mark Done on the right. */}
        <div className={styles.trayFooter}>
          {canSnooze && (
            <div className={styles.trayFooterSecondary}>
              <span className={styles.traySnoozeLabel}>
                {t("actionCenter.inbox.action.snoozeLabel", { defaultValue: "Snooze" } as never)}
              </span>
              {[1, 3, 7].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={styles.traySnoozeChip}
                  disabled={busy}
                  onClick={() => handlers.snoozeRhythm(item, d)}
                >
                  {t(`actionCenter.inbox.action.snoozeDays`, { days: d, defaultValue: `${d}d` } as never)}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy}
            onClick={() => handlers.completeRhythm(item)}
            style={{ marginInlineStart: canSnooze ? 0 : "auto" }}
          >
            {standing
              ? t("actionCenter.inbox.action.logOne")
              : t("actionCenter.inbox.action.markDone")}
          </button>
        </div>
      </>
    );
  }

  if (item.kind === "mp_task" && item.mpTask) {
    const task = item.mpTask.task;
    const existingKeys = task.evidenceAttachmentKeys ?? [];
    const existingFileNames = existingKeys.map((k) => {
      // Storage key shape: "{category}/{owner-guid}/{filename}". Pull the
      // tail segment for display; full key shows in the hover title.
      const tail = k.lastIndexOf("/");
      return { key: k, name: tail >= 0 ? k.slice(tail + 1) : k };
    });
    const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      // Reset the input so re-picking the same file fires onChange again.
      e.target.value = "";
      // Fire-and-forget — ctx.onFilesPicked manages busy + error state.
      void ctx.onFilesPicked(picked);
    };

    return (
      <>
        {item.description && <p>{item.description}</p>}
        {!item.description && <p>{t("actionCenter.inbox.tray.taskBody")}</p>}
        {task.howToUrl && (
          <p style={{ fontSize: 12, marginTop: 8 }}>
            <a
              href={task.howToUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--text-link, var(--info-text))" }}
            >
              <i className="ti ti-external-link" aria-hidden="true" />{" "}
              {t("actionCenter.inbox.tray.taskHowTo")}
            </a>
          </p>
        )}

        {/* DEV-1257. Existing evidence — chip list with a × delete button on
            each (DEV-1257-followup). Confirm before removing to prevent
            an accidental click destroying a deliberate upload. Server uses
            CanUpdateOwnTaskStatus so the assignee can remove their own
            mistakes (not only Leads). */}
        {existingFileNames.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {t("actionCenter.inbox.tray.taskExistingEvidence")}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {existingFileNames.map((f) => (
                <span
                  key={f.key}
                  title={f.key}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    padding: "2px 4px 2px 8px",
                    background: "var(--bg-tertiary)",
                    border: "1px solid var(--border-default)",
                    borderRadius: 4,
                    color: "var(--text-secondary)",
                  }}
                >
                  📎 {f.name}
                  <button
                    type="button"
                    disabled={ctx.busy}
                    onClick={() => {
                      if (
                        window.confirm(
                          t("actionCenter.inbox.tray.taskRemoveEvidenceConfirm", {
                            name: f.name,
                            defaultValue: `Remove ${f.name}?`,
                          } as never),
                        )
                      ) {
                        handlers.removeMpTaskEvidence(item, f.key);
                      }
                    }}
                    aria-label={t("actionCenter.inbox.tray.taskRemoveEvidenceAria", {
                      name: f.name,
                      defaultValue: `Remove ${f.name}`,
                    } as never)}
                    title={t("actionCenter.inbox.tray.taskRemoveEvidenceAria", {
                      name: f.name,
                      defaultValue: `Remove ${f.name}`,
                    } as never)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--text-secondary)",
                      cursor: ctx.busy ? "not-allowed" : "pointer",
                      padding: "0 4px",
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* DEV-1257. Evidence picker — always available (Option (b) lock).
            Selected files stay browser-local until Done; we never upload
            speculatively. Server enforces requires_evidence at transition. */}
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label
              style={{
                fontSize: 13,
                padding: "6px 10px",
                background: "var(--bg-tertiary)",
                border: "1px solid var(--border-default)",
                borderRadius: 4,
                cursor: busy ? "not-allowed" : "pointer",
                color: "var(--text-primary)",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <i className="ti ti-paperclip" aria-hidden="true" style={{ marginInlineEnd: 6 }} />
              {t("actionCenter.inbox.tray.taskAttachFiles")}
              <input
                type="file"
                multiple
                disabled={busy}
                onChange={onFilesPicked}
                style={{ display: "none" }}
                accept={EVIDENCE_ACCEPT_ATTR}
              />
            </label>
            {task.requiresEvidence && existingKeys.length === 0 && ctx.files.length === 0 && (
              <span style={{ fontSize: 12, color: "var(--color-warning)" }}>
                {t("actionCenter.inbox.tray.taskEvidenceRequired")}
              </span>
            )}
          </div>
          {/* DEV-1257-followup. Show the policy up front so users know what
              they can attach before opening the picker. Mirrors the backend
              allowlist (FileStorageController CategoryPolicies). */}
          <p style={{ fontSize: 11, color: "var(--text-tertiary)", margin: "4px 0 0" }}>
            {t("actionCenter.inbox.tray.taskAttachHint", {
              maxMb: Math.round(EVIDENCE_MAX_BYTES / 1024 / 1024),
              defaultValue: `Up to ${Math.round(EVIDENCE_MAX_BYTES / 1024 / 1024)} MB · images, PDFs, documents, spreadsheets`,
            } as never)}
          </p>
          {/* DEV-1257-followup. In-flight indicator only — files no longer
              queue waiting for Done. Each pick uploads immediately. The
              names land in ctx.files transiently so the user sees what's
              being uploaded; on success they vanish from this list and
              appear in the "Already attached" chips above. */}
          {ctx.uploadBusy && ctx.files.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
              {ctx.files.map((f, idx) => (
                <div
                  key={`${f.name}-${idx}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    padding: "4px 8px",
                    background: "var(--bg-tertiary)",
                    borderRadius: 4,
                    color: "var(--text-secondary)",
                  }}
                >
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    ↑ {f.name}
                  </span>
                  <span style={{ color: "var(--text-tertiary)" }}>{Math.round(f.size / 1024)} KB</span>
                </div>
              ))}
            </div>
          )}
          {ctx.uploadBusy && (
            <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8 }}>
              {t("actionCenter.inbox.tray.taskUploading")}
            </p>
          )}
          {ctx.uploadError && (
            <p style={{ fontSize: 12, color: "var(--color-error)", marginTop: 8 }}>
              {t("actionCenter.inbox.tray.taskUploadError")}: {ctx.uploadError}
            </p>
          )}
        </div>

        {/* Comment textarea — always available per Option (b) lock. */}
        <textarea
          className={styles.replyNote}
          rows={2}
          maxLength={2000}
          placeholder={t("actionCenter.inbox.tray.completionNotePlaceholder")}
          value={ctx.note}
          onChange={(e) => ctx.setNote(e.target.value)}
          disabled={busy}
          style={{ marginTop: 12 }}
        />

        {/* DEV-1257-followup. Structured footer — single Mark Done primary.
            "Open Project" navigation moved inline to the Source link in the
            meta prose at the top of the tray (Q3=(b) lock). */}
        <div className={styles.trayFooter}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy}
            onClick={ctx.onMpTaskDone}
            style={{ marginInlineStart: "auto" }}
          >
            {t("actionCenter.inbox.action.markDone")}
          </button>
        </div>
      </>
    );
  }

  if (item.kind === "data" && item.data) {
    const { section, projectGuid, row } = item.data;
    const grid =
      section === "schedule" ? (
        <ScheduleDataEntry
          projectGuid={projectGuid}
          scheduleInstances={row?.action_center_schedule_instances || []}
          latestPointsByInstance={row?.action_center_latest_schedule_points || {}}
          canEdit={!!row?.action_center_can_edit_kpi}
          onDataSaved={handlers.onDataSaved}
          variant="stacked"
        />
      ) : section === "finance" ? (
        <BudgetDataEntry
          projectGuid={projectGuid}
          financeInstances={row?.action_center_finance_instances || []}
          latestPointsByInstance={row?.action_center_latest_finance_points || {}}
          canEdit={!!row?.action_center_can_edit_kpi}
          onDataSaved={handlers.onDataSaved}
          variant="stacked"
        />
      ) : (
        <MissionDataEntry
          projectGuid={projectGuid}
          missionSubkpis={excludeHse(row?.mission_subkpis || [])}
          projectTargets={row?.mission_targets || {}}
          canEdit={!!row?.action_center_can_edit_kpi}
          onDataSaved={handlers.onDataSaved}
          variant="stacked"
        />
      );
    return (
      <>
        {grid}
        {/* DEV-1257-followup. Open Project moves to a small inline link
            near the bottom; the entry grid itself owns the Save action
            (no separate Save button in the tray). */}
        {projectGuid && (
          <button
            type="button"
            className={styles.trayInlineLink}
            onClick={() => handlers.openProject(projectGuid)}
          >
            ↗ {t("actionCenter.inbox.action.openProject")}
          </button>
        )}
      </>
    );
  }

  // Gantt R (Responsible) project task (DEV-1368). Read-only detail in the
  // do-list tray — the editable %/Done/flag actions live in the role's Digest
  // tab (RoleDigestView). "show now, actions next" (Skip 2026-06-23).
  if (item.kind === "raci" && item.raciTask) {
    const rt = item.raciTask;
    const meta = [`${rt.percentComplete}%`, item.dateLabel && item.dateLabel !== "—" ? item.dateLabel : null]
      .filter(Boolean)
      .join(" · ");
    return (
      <>
        <p>{rt.wbsPath ? `${rt.wbsPath} › ${rt.taskName}` : rt.taskName}</p>
        {/* Project name + progress/due. item.source is the project (DEV-1368). */}
        <p className={styles.trayHint}>
          {item.source}
          {meta ? ` · ${meta}` : ""}
        </p>
        <div className={styles.trayFooter}>
          {rt.projectGuid && (
            <button
              type="button"
              className={styles.trayInlineLink}
              onClick={() => handlers.openProject(rt.projectGuid)}
            >
              ↗ {t("actionCenter.inbox.action.openProject")}
            </button>
          )}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy}
            onClick={() => handlers.completeRaci(item)}
            style={{ marginInlineStart: "auto" }}
          >
            {t("actionCenter.inbox.action.markDone")}
          </button>
        </div>
      </>
    );
  }

  // system task (onboarding "Setup" milestone). Completion is system-detected
  // (the milestone evaluator), so there's no manual "done" — just the real
  // nudge text + a CTA that takes the user where they can complete it.
  const ms = (item.milestone || {}) as {
    nudgeKey?: string;
    nudgeText?: string;
    ctaLabelKey?: string;
  };
  const nudge = ms.nudgeText || (ms.nudgeKey ? t(ms.nudgeKey) : t("actionCenter.inbox.tray.systemBody"));
  const ctaLabel = ms.ctaLabelKey ? t(ms.ctaLabelKey) : t("actionCenter.inbox.action.go");
  return (
    <>
      <p>{nudge}</p>
      <div className={styles.trayFooter}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={busy}
          onClick={() => handlers.openSystemCta(item)}
          style={{ marginInlineStart: "auto" }}
        >
          {ctaLabel}
        </button>
      </div>
    </>
  );
}

export default ActionItemTray;
